import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlagIcon from '@mui/icons-material/Flag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchAirtableModerations,
  fetchCriteria,
  fetchStylusJudgementById,
  flagInaccurateModerations,
  unflagModerations,
} from '../../services/api';
import { AirtableModeration, Criteria, LabelType, StylusJudgement } from '../../types';
import ClassificationChip, { PURPLE_SX } from '../common/ClassificationChip';
import ColumnFilter, { ColumnFilterRef } from '../common/ColumnFilter';
import SearchableSelect from '../common/SearchableSelect';
import EssayContent from '../EssayContent';
import SortableTable, { HeadCell } from '../common/SortableTable';

const Moderation: React.FC = () => {
  const [criteriaList, setCriteriaList] = useState<Criteria[]>([]);
  const [selectedCriteriaId, setSelectedCriteriaId] = useState('');
  const [moderations, setModerations] = useState<AirtableModeration[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCriteria, setLoadingCriteria] = useState(true);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const filterRefs = useRef<Record<string, ColumnFilterRef>>({});

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogJudgement, setDialogJudgement] = useState<StylusJudgement | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [selectedSentenceIndex, setSelectedSentenceIndex] = useState<number | null>(null);

  useEffect(() => {
    fetchCriteria()
      .then(data => setCriteriaList(data))
      .finally(() => setLoadingCriteria(false));
  }, []);

  useEffect(() => {
    if (!selectedCriteriaId) {
      setModerations([]);
      return;
    }
    setLoading(true);
    setFilters({});
    Object.values(filterRefs.current).forEach(ref => ref?.reset());
    fetchAirtableModerations(selectedCriteriaId)
      .then(data => setModerations(data))
      .catch(() => setModerations([]))
      .finally(() => setLoading(false));
  }, [selectedCriteriaId]);

  const handleToggleFlagged = useCallback(async (m: AirtableModeration) => {
    if (m.isFlaggedInaccurate) {
      await unflagModerations([m.airtableRecordId]);
    } else {
      await flagInaccurateModerations([m.airtableRecordId]);
    }
    setModerations(prev =>
      prev.map(x =>
        x.airtableRecordId === m.airtableRecordId
          ? { ...x, isFlaggedInaccurate: !m.isFlaggedInaccurate }
          : x
      )
    );
  }, []);

  const handleViewJudgement = useCallback((judgementId: string) => {
    setDialogOpen(true);
    setDialogLoading(true);
    setDialogJudgement(null);
    setSelectedSentenceIndex(null);
    fetchStylusJudgementById(judgementId)
      .then(data => setDialogJudgement(data))
      .catch(() => setDialogJudgement(null))
      .finally(() => setDialogLoading(false));
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setDialogJudgement(null);
    setSelectedSentenceIndex(null);
  }, []);

  const handleSentenceClick = useCallback((_fragment: string, index: number) => {
    setSelectedSentenceIndex(prev => (prev === index ? null : index));
  }, []);

  const getLabelTypeForFragment = useCallback(
    (index: number): LabelType | null => {
      if (!dialogJudgement?.sentences) return null;
      return dialogJudgement.sentences[index]?.label as LabelType | null;
    },
    [dialogJudgement]
  );

  const selectedSentence =
    selectedSentenceIndex !== null ? dialogJudgement?.sentences[selectedSentenceIndex] : null;

  const handleFilterChange = useCallback((id: string, value: any) => {
    setFilters(prev => {
      if (value === '') {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: value };
    });
  }, []);

  const handleClearFilter = useCallback((filterId: string) => {
    setFilters(prev => {
      const next = { ...prev };
      delete next[filterId];
      return next;
    });
    filterRefs.current[filterId]?.reset();
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    Object.values(filterRefs.current).forEach(ref => ref?.reset());
  }, []);

  const registerFilterRef = useCallback((id: string, ref: ColumnFilterRef | null) => {
    if (ref) {
      filterRefs.current[id] = ref;
    }
  }, []);

  const sortedCriteriaOptions = useMemo(
    () =>
      [...criteriaList]
        .filter(c => !c.isArchived || filters.criteriaId === c.id)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(c => ({ value: c.id, label: `${c.name} [${c.granularity}]` })),
    [criteriaList, filters.criteriaId]
  );

  const judgementOptions = useMemo(() => {
    const values = Array.from(
      new Set(moderations.map(m => m.judgement).filter(Boolean) as string[])
    );
    return values.sort().map(v => ({ id: v, label: v, value: v }));
  }, [moderations]);

  const reasonOptions = useMemo(() => {
    const values = Array.from(
      new Set(moderations.map(m => m.reasonForChange).filter(Boolean) as string[])
    );
    return values.sort().map(v => ({ id: v, label: v, value: v }));
  }, [moderations]);

  const filteredModerations = useMemo(() => {
    return moderations.filter(m => {
      if (filters.judgement) {
        const selected = Array.isArray(filters.judgement) ? filters.judgement : [filters.judgement];
        if (!selected.includes(m.judgement)) return false;
      }
      if (filters.reasonForChange) {
        const selected = Array.isArray(filters.reasonForChange)
          ? filters.reasonForChange
          : [filters.reasonForChange];
        if (!selected.includes(m.reasonForChange)) return false;
      }
      if (filters.contextForChange) {
        const term = filters.contextForChange.toLowerCase();
        if (!(m.contextForChange ?? '').toLowerCase().includes(term)) return false;
      }
      return true;
    });
  }, [moderations, filters]);

  const getFilterLabel = useCallback((id: string) => {
    const labels: Record<string, string> = {
      judgement: 'Judgement',
      reasonForChange: 'Reason',
      contextForChange: 'Context',
    };
    return labels[id] ?? id;
  }, []);

  const getFilterDescription = useCallback((_id: string, value: any) => {
    if (Array.isArray(value)) return `${value.length} selected`;
    return String(value);
  }, []);

  const headCells: HeadCell<AirtableModeration>[] = useMemo(
    () => [
      {
        id: 'judgement',
        label: 'Judgement',
        numeric: false,
        sortable: true,
        width: '10%',
        renderCell: m => m.judgement ?? '-',
        getComparableValue: m => m.judgement ?? '',
        filter: (
          <ColumnFilter
            id="judgement"
            label="Judgement"
            type="dropdown"
            options={judgementOptions}
            onChange={handleFilterChange}
            multiple
            ref={ref => registerFilterRef('judgement', ref)}
          />
        ),
      },
      {
        id: 'reasonForChange',
        label: 'Reason for Change',
        numeric: false,
        sortable: true,
        width: '10%',
        renderCell: m => m.reasonForChange ?? '-',
        getComparableValue: m => m.reasonForChange ?? '',
        filter: (
          <ColumnFilter
            id="reasonForChange"
            label="Reason"
            type="dropdown"
            options={reasonOptions}
            onChange={handleFilterChange}
            multiple
            ref={ref => registerFilterRef('reasonForChange', ref)}
          />
        ),
      },
      {
        id: 'contextForChange',
        label: 'Context for Change',
        numeric: false,
        sortable: false,
        width: '15%',
        renderCell: m => m.contextForChange ?? '-',
        filter: (
          <ColumnFilter
            id="contextForChange"
            label="Search context"
            type="text"
            onChange={handleFilterChange}
            ref={ref => registerFilterRef('contextForChange', ref)}
          />
        ),
      },
      {
        id: 'commentType',
        label: 'Type',
        numeric: false,
        sortable: true,
        width: '5%',
        renderCell: m => m.commentType ?? '-',
        getComparableValue: m => m.commentType ?? '',
      },
      {
        id: 'originalComment',
        label: 'Original Comment',
        numeric: false,
        sortable: false,
        width: '25%',
        renderCell: m => m.originalComment ?? '-',
      },
      {
        id: 'createdAt',
        label: 'Created At',
        numeric: false,
        sortable: true,
        width: '10%',
        renderCell: m => (m.createdAt ? new Date(m.createdAt).toLocaleDateString() : '-'),
        getComparableValue: m => (m.createdAt ? new Date(m.createdAt).getTime() : 0),
      },
      {
        id: 'isFlaggedInaccurate',
        label: 'Inaccurate',
        numeric: false,
        sortable: true,
        width: '100px',
        renderCell: m => (
          <Tooltip title={m.isFlaggedInaccurate ? 'Unmark as inaccurate' : 'Mark as inaccurate'}>
            <IconButton
              size="small"
              color={m.isFlaggedInaccurate ? 'primary' : 'default'}
              onClick={e => {
                e.stopPropagation();
                handleToggleFlagged(m);
              }}
              aria-label="Mark moderator feedback as inaccurate"
            >
              {m.isFlaggedInaccurate ? (
                <FlagIcon fontSize="small" />
              ) : (
                <FlagOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        ),
        getComparableValue: m => (m.isFlaggedInaccurate ? 1 : 0),
      },
      {
        id: 'actions',
        label: '',
        numeric: false,
        sortable: false,
        width: '120px',
        renderCell: m => (
          <Button
            variant="outlined"
            size="small"
            onClick={e => {
              e.stopPropagation();
              handleViewJudgement(m.stylusJudgementId);
            }}
          >
            View Judgement
          </Button>
        ),
      },
    ],
    [
      judgementOptions,
      reasonOptions,
      handleFilterChange,
      registerFilterRef,
      handleViewJudgement,
      handleToggleFlagged,
    ]
  );

  const sentences = dialogJudgement?.sentences
    ? dialogJudgement.sentences.map(s => s.sentence)
    : [];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        Moderation
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Criterion</InputLabel>
          <SearchableSelect
            value={selectedCriteriaId}
            label="Criterion"
            onChange={e => setSelectedCriteriaId(e.target.value as string)}
            disabled={loadingCriteria}
            options={sortedCriteriaOptions}
          />
        </FormControl>

        {loading && <CircularProgress size={24} />}

        {selectedCriteriaId && !loading && (
          <Typography variant="body2" color="text.secondary">
            {moderations.length} moderation{moderations.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {!loading && selectedCriteriaId ? (
        <SortableTable
          data={filteredModerations}
          headCells={headCells}
          defaultSortBy="reasonForChange"
          defaultOrder="asc"
          idAccessor={m => m.airtableRecordId}
          emptyMessage="No moderations found for this criterion."
          showFilters
          filters={filters}
          onClearFilter={handleClearFilter}
          onClearAllFilters={handleClearAllFilters}
          getFilterLabel={getFilterLabel}
          getFilterDescription={getFilterDescription}
          totalCount={moderations.length}
        />
      ) : !selectedCriteriaId ? (
        <Typography color="text.secondary">Select a criterion to view moderations.</Typography>
      ) : null}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Sentence Classifications
            <Chip label="Positive" size="small" color="success" variant="outlined" />
            <Chip label="Negative" size="small" color="error" variant="outlined" />
            <Chip label="Both" size="small" variant="outlined" sx={PURPLE_SX} />
          </Box>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {dialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : sentences.length > 0 ? (
            <>
              <Box sx={{ mb: selectedSentence ? 2 : 0 }}>
                <EssayContent
                  fragments={sentences}
                  selectedFragmentIndex={selectedSentenceIndex}
                  handleFragmentClick={handleSentenceClick}
                  getLabelTypeForFragment={getLabelTypeForFragment}
                />
              </Box>
              {selectedSentence && (
                <Box sx={{ pt: 1, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                  {selectedSentence.label && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2">Classification:</Typography>
                      <ClassificationChip value={selectedSentence.label} />
                    </Box>
                  )}
                  {selectedSentence.reason && (
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.03)',
                        borderLeft: '3px solid rgba(0, 0, 0, 0.2)',
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
                      >
                        AI reasoning
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedSentence.reason}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No judgement data found.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Moderation;
